// PAT-14 — persisted account preferences (localStorage + optional Claude PostgreSQL sync).
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { AuthService } from './auth.service';
import { AiChatApiService } from './ai-chat-api.service';
import { ThemeService, ThemePreference } from './theme.service';
import { FontSizeService, FontSizePreference } from './font-size.service';
import { SsoCryptoService } from './sso-crypto.service';
import { environment } from '../../environments/environment';
import { buildDefaultModelPickerLabel } from '../utils/default-model-label.util'; // PAT-495

export const FILE_UPLOAD_TYPE_OPTIONS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'pdf', label: 'PDF documents' },
  { id: 'images', label: 'Images (PNG, JPG, GIF)' },
  { id: 'word', label: 'Microsoft Word' },
  { id: 'excel', label: 'Spreadsheets (Excel, CSV)' },
  { id: 'text', label: 'Plain text' },
];

/** Stored default model (matches catalog row + `auto`). */
export interface DefaultModelSelection {
  modelKey: string;
  displayName: string;
  providerDisplayName: string;
}

export interface AccountPreferences {
  firstName: string;
  lastName: string;
  defaultModel: DefaultModelSelection;
  /** Subset of FILE_UPLOAD_TYPE_OPTIONS ids. */
  fileUploadTypeIds: string[];
}

/** PAT-203 — server-backed appearance prefs (also cached in localStorage via theme/font services). */
export interface AppearancePreferences {
  themePreference: ThemePreference;
  fontSize: FontSizePreference;
}

const STORAGE_VER = 'pathways_account_prefs_v1';
const COACH_TIP_STORAGE = 'pathways_skills_coach_tip_v1';

function splitNameFromEmail(email: string): { first: string; last: string } {
  const local = (email.split('@')[0] ?? '').trim();
  const cleaned = local.replace(/[._]/g, ' ').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return {
      first: parts[0]!.replace(/\b\w/g, c => c.toUpperCase()),
      last: parts.slice(1).join(' ').replace(/\b\w/g, c => c.toUpperCase()),
    };
  }
  if (parts[0]) {
    const p = parts[0].replace(/\b\w/g, c => c.toUpperCase());
    return { first: p, last: '' };
  }
  return { first: '', last: '' };
}

type StoredPrefsInput = Partial<AccountPreferences> & {
  defaultModelKey?: string;
  /** Flat model row (matches some GET /account-preferences payloads). */
  modelKey?: string;
  displayName?: string;
  providerDisplayName?: string;
};

/**
 * GET `/api/chat/account-preferences` response (Claude service).
 *
 * Canonical shape:
 * ```json
 * {
 *   "success": true,
 *   "defaultModel": {
 *     "modelKey": "claude-sonnet-4-6",
 *     "displayName": "Claude Sonnet 4.6",
 *     "providerDisplayName": "Anthropic"
 *   },
 *   "fileUploadTypeIds": ["pdf", "text"]
 * }
 * ```
 *
 * Optional flat `modelKey` / `displayName` / `providerDisplayName` at root are accepted as a legacy fallback.
 *
 * Some gateways or raw DB echoes may use snake_case (`default_model`, `model_key`, …); normalize before merge.
 */
export interface AccountPreferencesGetResponse {
  success?: boolean;
  defaultModel?: {
    modelKey?: string;
    displayName?: string;
    providerDisplayName?: string;
  };
  fileUploadTypeIds?: string[];
  /** PAT-203 — portal appearance from GET /account-preferences */
  themePreference?: string;
  fontSize?: string;
  theme_preference?: string;
  font_size?: string;
  modelKey?: string;
  displayName?: string;
  providerDisplayName?: string;
  /** PAT-270 — skills discoverability walkthrough seen flag */
  hasSeenSkillsWalkthrough?: boolean;
  has_seen_skills_walkthrough?: boolean;
}

/** Map GET `/account-preferences` JSON (camelCase or snake_case) into {@link AccountPreferencesGetResponse}. */
export function normalizeAccountPreferencesGetResponse(raw: unknown): AccountPreferencesGetResponse {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const r = raw as Record<string, unknown>;
  const out: AccountPreferencesGetResponse = {};
  if (typeof r['success'] === 'boolean') {
    out.success = r['success'];
  }
  const dmRaw = r['defaultModel'] ?? r['default_model'];
  if (dmRaw && typeof dmRaw === 'object' && !Array.isArray(dmRaw)) {
    const m = dmRaw as Record<string, unknown>;
    const mk =
      (typeof m['modelKey'] === 'string' ? m['modelKey'] : undefined) ??
      (typeof m['model_key'] === 'string' ? m['model_key'] : undefined);
    const dn =
      (typeof m['displayName'] === 'string' ? m['displayName'] : undefined) ??
      (typeof m['display_name'] === 'string' ? m['display_name'] : undefined) ??
      '';
    const pd =
      (typeof m['providerDisplayName'] === 'string' ? m['providerDisplayName'] : undefined) ??
      (typeof m['provider_display_name'] === 'string' ? m['provider_display_name'] : undefined) ??
      '';
    out.defaultModel = {
      ...(mk !== undefined ? { modelKey: mk } : {}),
      displayName: dn,
      providerDisplayName: pd,
    };
  }
  const fu = r['fileUploadTypeIds'] ?? r['file_upload_type_ids'];
  if (Array.isArray(fu)) {
    out.fileUploadTypeIds = fu.filter((x): x is string => typeof x === 'string');
  }
  const themeRaw = r['themePreference'] ?? r['theme_preference'];
  if (typeof themeRaw === 'string') {
    out.themePreference = themeRaw;
  }
  const fontRaw = r['fontSize'] ?? r['font_size'];
  if (typeof fontRaw === 'string') {
    out.fontSize = fontRaw;
  }
  const flatMk =
    (typeof r['modelKey'] === 'string' ? r['modelKey'] : undefined) ??
    (typeof r['model_key'] === 'string' ? r['model_key'] : undefined);
  if (flatMk !== undefined) {
    out.modelKey = flatMk;
  }
  const flatDn =
    (typeof r['displayName'] === 'string' ? r['displayName'] : undefined) ??
    (typeof r['display_name'] === 'string' ? r['display_name'] : undefined);
  if (flatDn !== undefined) {
    out.displayName = flatDn;
  }
  const flatPd =
    (typeof r['providerDisplayName'] === 'string' ? r['providerDisplayName'] : undefined) ??
    (typeof r['provider_display_name'] === 'string' ? r['provider_display_name'] : undefined);
  if (flatPd !== undefined) {
    out.providerDisplayName = flatPd;
  }
  const seenRaw = r['hasSeenSkillsWalkthrough'] ?? r['has_seen_skills_walkthrough'];
  if (typeof seenRaw === 'boolean') {
    out.hasSeenSkillsWalkthrough = seenRaw;
  }
  return out;
}

function normalizeDefaultModel(raw: StoredPrefsInput): DefaultModelSelection {
  const dm = raw.defaultModel;
  if (dm && typeof dm === 'object' && !Array.isArray(dm)) {
    const o = dm as unknown as Record<string, unknown>;
    const mkRaw = o['modelKey'];
    const mk =
      typeof mkRaw === 'string' && mkRaw.trim() ? mkRaw.trim() : 'auto';
    return {
      modelKey: mk,
      displayName: typeof o['displayName'] === 'string' ? o['displayName'] : '',
      providerDisplayName:
        typeof o['providerDisplayName'] === 'string' ? o['providerDisplayName'] : '',
    };
  }
  const flatMk = raw.modelKey;
  if (typeof flatMk === 'string' && flatMk.trim()) {
    return {
      modelKey: flatMk.trim(),
      displayName: typeof raw.displayName === 'string' ? raw.displayName : '',
      providerDisplayName:
        typeof raw.providerDisplayName === 'string' ? raw.providerDisplayName : '',
    };
  }
  const legacy =
    typeof raw.defaultModelKey === 'string' && raw.defaultModelKey.trim()
      ? raw.defaultModelKey.trim()
      : '';
  if (legacy) {
    return {
      modelKey: legacy,
      displayName: '',
      providerDisplayName: '',
    };
  }
  return {
    modelKey: 'auto',
    displayName: buildDefaultModelPickerLabel(),
    providerDisplayName: '',
  };
}

function normalizeThemePreference(raw: unknown): ThemePreference {
  const s = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (s === 'light' || s === 'dark' || s === 'system') return s;
  return 'light';
}

function normalizeFontSizePreference(raw: unknown): FontSizePreference {
  const s = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (s === 's' || s === 'm' || s === 'l') return s;
  return 'm';
}

function appearanceFromRemoteDto(data: AccountPreferencesGetResponse): AppearancePreferences {
  return {
    themePreference: normalizeThemePreference(data.themePreference),
    fontSize: normalizeFontSizePreference(data.fontSize),
  };
}

function mergeRemoteFileUploadTypeIds(
  data: AccountPreferencesGetResponse,
  prev: AccountPreferences | null,
): string[] {
  if (Object.prototype.hasOwnProperty.call(data, 'fileUploadTypeIds') && Array.isArray(data.fileUploadTypeIds)) {
    return [...data.fileUploadTypeIds];
  }
  return prev?.fileUploadTypeIds?.length ? [...prev.fileUploadTypeIds] : [];
}

/** Maps {@link AccountPreferencesGetResponse} into {@link StoredPrefsInput} for merge. */
function storedInputFromRemoteDto(
  data: AccountPreferencesGetResponse,
  prev: AccountPreferences | null,
): StoredPrefsInput {
  const base: StoredPrefsInput = {
    firstName: prev?.firstName ?? '',
    lastName: prev?.lastName ?? '',
    fileUploadTypeIds: mergeRemoteFileUploadTypeIds(data, prev),
  };
  if (data.defaultModel && typeof data.defaultModel === 'object') {
    base.defaultModel = {
      modelKey: (data.defaultModel.modelKey ?? '').trim() || 'auto',
      displayName: data.defaultModel.displayName ?? '',
      providerDisplayName: data.defaultModel.providerDisplayName ?? '',
    };
    return base;
  }
  if (typeof data.modelKey === 'string' && data.modelKey.trim()) {
    return {
      ...base,
      modelKey: data.modelKey.trim(),
      displayName: typeof data.displayName === 'string' ? data.displayName : '',
      providerDisplayName:
        typeof data.providerDisplayName === 'string' ? data.providerDisplayName : '',
    };
  }
  return base;
}

function normalizePrefs(raw: StoredPrefsInput | null): AccountPreferences | null {
  if (!raw || typeof raw !== 'object') return null;
  const firstName = typeof raw.firstName === 'string' ? raw.firstName.trim() : '';
  const lastName = typeof raw.lastName === 'string' ? raw.lastName.trim() : '';
  const defaultModel = normalizeDefaultModel(raw);
  const allowed = new Set(FILE_UPLOAD_TYPE_OPTIONS.map(o => o.id));
  /** PAT-14 — Missing `fileUploadTypeIds` in stored JSON → not configured (all types). Explicit `[]` disables chat attach. */
  let ids: string[];
  if (!Array.isArray(raw.fileUploadTypeIds)) {
    ids = FILE_UPLOAD_TYPE_OPTIONS.map(o => o.id);
  } else {
    ids = raw.fileUploadTypeIds.filter((id): id is string => typeof id === 'string' && allowed.has(id));
  }
  return {
    firstName,
    lastName,
    defaultModel,
    fileUploadTypeIds: [...new Set(ids)],
  };
}

/** PAT-14 — Resolved allowed upload category ids: no prefs → all types; explicit empty → none (chat attach disabled). */
export function resolveFileUploadTypeIds(stored: AccountPreferences | null): string[] {
  if (stored === null) {
    return FILE_UPLOAD_TYPE_OPTIONS.map(o => o.id);
  }
  return [...stored.fileUploadTypeIds];
}

@Injectable({ providedIn: 'root' })
export class AccountPreferencesService {
  private readonly auth = inject(AuthService);
  private readonly aiChat = inject(AiChatApiService);
  private readonly themeService = inject(ThemeService);
  private readonly fontSizeService = inject(FontSizeService);
  private readonly crypto = inject(SsoCryptoService);

  private prefsSignal = signal<AccountPreferences | null>(null);
  /** PAT-270 — server-backed walkthrough seen flag. */
  private readonly seenSkillsWalkthrough = signal(false);
  /** PAT-270 — show coach tip after skip until dismissed. */
  private readonly showSkillsCoachTip = signal(false);
  /**
   * PAT-348 — true after walkthrough prefs are resolved from GET (or sync skipped).
   * Auto-open must wait so a prior DB `hasSeenSkillsWalkthrough: true` is not ignored.
   */
  private readonly skillsWalkthroughPrefsReadySignal = signal(!environment.claudeDbSyncEnabled);
  /** PAT-348 — last email we hydrated walkthrough prefs for (reset gate on account switch). */
  private lastWalkthroughPrefsEmail = '';

  readonly preferences = this.prefsSignal.asReadonly();
  readonly hasSeenSkillsWalkthrough = this.seenSkillsWalkthrough.asReadonly();
  readonly skillsCoachTipVisible = this.showSkillsCoachTip.asReadonly();
  readonly skillsWalkthroughPrefsReady = this.skillsWalkthroughPrefsReadySignal.asReadonly();

  readonly defaultModelForNewChats = computed(
    () => this.prefsSignal()?.defaultModel.modelKey ?? 'auto',
  );

  /** PAT-14 — Category ids for chat file picker (`accept` + validation); empty disables attach. */
  readonly allowedFileUploadTypeIds = computed(() => resolveFileUploadTypeIds(this.prefsSignal()));

  constructor() {
    this.reloadFromStorage();
    // PAT-14 — Constructor often runs before AuthService has hydrated the user. Reload prefs when
    // the signed-in email appears so localStorage defaultModel applies to New Chat immediately
    // (not only after GET /account-preferences completes).
    effect(() => {
      const email = this.auth.user()?.email?.trim().toLowerCase();
      if (!email) {
        this.lastWalkthroughPrefsEmail = '';
        this.prefsSignal.set(null);
        this.seenSkillsWalkthrough.set(false);
        this.showSkillsCoachTip.set(false);
        this.skillsWalkthroughPrefsReadySignal.set(!environment.claudeDbSyncEnabled);
        return;
      }
      if (email !== this.lastWalkthroughPrefsEmail) {
        this.lastWalkthroughPrefsEmail = email;
        // PAT-348 — wait for GET before auto-open when DB sync is enabled.
        this.seenSkillsWalkthrough.set(false);
        this.skillsWalkthroughPrefsReadySignal.set(!environment.claudeDbSyncEnabled);
      }
      this.reloadFromStorage();
    });
  }

  reloadFromStorage(): void {
    const email = this.auth.user()?.email?.trim().toLowerCase();
    if (!email) {
      this.prefsSignal.set(null);
      return;
    }
    const key = `${STORAGE_VER}:${btoa(email)}`;
    try {
      let raw = localStorage.getItem(key) ?? null;
      // PAT-385 — migrate from earlier key variants to the btoa-hashed key.
      if (!raw) {
        // Variant 1: temporary fixed key written during this sprint (no email suffix).
        const fixedRaw = localStorage.getItem(STORAGE_VER);
        // Variant 2: original plain-email key (pre-PAT-385).
        const plainRaw = localStorage.getItem(`${STORAGE_VER}:${email}`);
        const legacyRaw = fixedRaw ?? plainRaw;
        if (legacyRaw) {
          raw = legacyRaw;
          try {
            localStorage.setItem(key, legacyRaw);
            if (fixedRaw) localStorage.removeItem(STORAGE_VER);
            if (plainRaw) localStorage.removeItem(`${STORAGE_VER}:${email}`);
          } catch { /* ignore */ }
        }
      }
      if (!raw) {
        this.prefsSignal.set(null);
        return;
      }
      // PAT-385 — decrypt before parsing; migration path handles legacy plain-text records.
      const parsed = this.decryptAndParsePrefs(raw, key);
      this.prefsSignal.set(normalizePrefs(parsed));
    } catch {
      this.prefsSignal.set(null);
    }
  }

  /**
   * PAT-385 — Decrypt an AES-encrypted account-prefs record from localStorage.
   * Migration path: if decryption fails (legacy plain-text record), falls back to JSON.parse
   * and immediately re-persists encrypted under the same key.
   */
  private decryptAndParsePrefs(raw: string, key: string): StoredPrefsInput | null {
    let json = raw;
    let rewritePlain = false;
    try {
      const decrypted = this.crypto.decryptAES(raw).trim();
      if (!decrypted) throw new Error('empty');
      json = decrypted;
      rewritePlain = true;
    } catch {
      json = raw;
    }
    let parsed: StoredPrefsInput;
    try {
      parsed = JSON.parse(json) as StoredPrefsInput;
    } catch {
      return null;
    }
    if (rewritePlain) {
      try {
        localStorage.setItem(key, JSON.stringify(parsed));
      } catch { /* ignore */ }
    }
    return parsed;
  }

  /**
   * Resolved defaults for the form: stored prefs merged with JWT/session display data.
   */
  resolvedPreferences(): AccountPreferences {
    const u = this.auth.user();
    const stored = this.prefsSignal();
    const email = u?.email ?? '';
    const fromJwt = u?.userName?.trim();
    let first = stored?.firstName ?? '';
    let last = stored?.lastName ?? '';
    if (!first && !last && fromJwt) {
      const bits = fromJwt.split(/\s+/).filter(Boolean);
      if (bits.length >= 2) {
        first = bits[0] ?? '';
        last = bits.slice(1).join(' ');
      } else if (bits.length === 1) {
        first = bits[0] ?? '';
      }
    }
    if (!first && !last && email) {
      const sp = splitNameFromEmail(email);
      first = sp.first;
      last = sp.last;
    }
    const defaultModel = stored
      ? normalizeDefaultModel(stored)
      : {
          modelKey: 'auto',
          displayName: buildDefaultModelPickerLabel(),
          providerDisplayName: '',
        };
    // PAT-14 — fileUploadTypeIds drives account settings + chat attach allow-list.
    const fileUploadTypeIds = resolveFileUploadTypeIds(stored);

    return {
      firstName: first,
      lastName: last,
      defaultModel,
      fileUploadTypeIds,
    };
  }

  /**
   * Writes localStorage first, then PUTs to orchestrator when `claudeDbSyncEnabled` and session has token.
   * PAT-203 — optional appearance fields are persisted with model/file prefs.
   */
  async save(
    prefs: AccountPreferences,
    appearance?: AppearancePreferences,
  ): Promise<void> {
    const email = this.auth.user()?.email?.trim().toLowerCase();
    if (!email) return;
    const normalized = normalizePrefs(prefs);
    if (!normalized) return;
    const key = `${STORAGE_VER}:${btoa(email)}`;
    try {
      localStorage.setItem(key, JSON.stringify(normalized));
      this.prefsSignal.set(normalized);
    } catch {
      /* quota / private mode */
    }
    const appearancePayload = appearance ?? {
      themePreference: this.themeService.themePreference(),
      fontSize: this.fontSizeService.fontSize(),
    };
    if (!environment.claudeDbSyncEnabled || !this.auth.isLoggedIn()) {
      return;
    }
    try {
      const res = await this.aiChat.putAccountPreferences(environment.chatApiBaseUrl, {
        defaultModel: {
          modelKey: normalized.defaultModel.modelKey,
          displayName: normalized.defaultModel.displayName,
          providerDisplayName: normalized.defaultModel.providerDisplayName,
        },
        fileUploadTypeIds: normalized.fileUploadTypeIds,
        themePreference: appearancePayload.themePreference,
        fontSize: appearancePayload.fontSize,
        // PAT-270 — omit so account-settings saves do not reset the flag.
      });
      if (!res.ok) {
        console.warn(
          'Account preferences: server save failed',
          res.status,
          await res.text().catch(() => ''),
        );
      }
    } catch (e) {
      console.warn('Account preferences: server save error', e);
    }
  }

  /**
   * Loads prefs from PostgreSQL and merges into localStorage + signal (names stay local).
   * PAT-203 — also applies theme + font size from the server when present.
   * PAT-348 — hydrates `hasSeenSkillsWalkthrough` into the walkthrough gate signal.
   * Call after login or when opening account settings.
   */
  async syncFromRemoteIfEnabled(): Promise<void> {
    if (!environment.claudeDbSyncEnabled) {
      this.skillsWalkthroughPrefsReadySignal.set(true);
      return;
    }
    const u = this.auth.user();
    const email = u?.email?.trim().toLowerCase();
    if (!email || !this.auth.isLoggedIn()) {
      this.skillsWalkthroughPrefsReadySignal.set(true);
      return;
    }
    try {
      const res = await this.aiChat.getAccountPreferences(environment.chatApiBaseUrl);
      if (!res.ok) return;
      const data = normalizeAccountPreferencesGetResponse(await res.json());
      if (data.success === false) return;
      // PAT-348 — apply walkthrough flag before prefs merge so a failed merge cannot skip hydration.
      const seen = data.hasSeenSkillsWalkthrough === true;
      this.seenSkillsWalkthrough.set(seen);
      this.refreshCoachTipVisibility(email, seen);
      const prev = this.prefsSignal();
      const merged = normalizePrefs(storedInputFromRemoteDto(data, prev));
      if (!merged) return;
      const key = `${STORAGE_VER}:${btoa(email)}`;
      try {
        localStorage.setItem(key, JSON.stringify(merged));
      } catch {
        /* quota */
      }
      this.prefsSignal.set(merged);
      const appearance = appearanceFromRemoteDto(data);
      // this.themeService.setThemePreference(appearance.themePreference);
      // this.fontSizeService.setFontSize(appearance.fontSize);
      // Only apply remote appearance values when no local preference is stored yet
      // (e.g. first login on a fresh device).  If the user has already set a theme
      // locally — whether via Account Settings or the sidebar quick-toggle — their
      // local choice must take precedence.  Unconditionally overwriting it would
      // revert an in-progress toggle the moment Account Settings is opened.
      const hasLocalTheme =
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('pathways-theme') !== null;
      if (!hasLocalTheme) {
        this.themeService.setThemePreference(appearance.themePreference);
      }
      const hasLocalFontSize =
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('pathways-font-size') !== null;
      if (!hasLocalFontSize) {
        this.fontSizeService.setFontSize(appearance.fontSize);
      }
      // PAT-270 — apply server-side walkthrough seen flag so the walkthrough does
      // not auto-reopen on a fresh device or after cache clears.
      if (data.hasSeenSkillsWalkthrough === true) {
        this.seenSkillsWalkthrough.set(true);
        this.refreshCoachTipVisibility(email, true);
      }
    } catch {
      /* offline or CORS */
    } finally {
      // PAT-348 — unblock auto-open after attempt (success or fail).
      this.skillsWalkthroughPrefsReadySignal.set(true);
    }
  }

  /**
   * PAT-270 — Persist walkthrough completion/skip server-side.
   * When skipped, also enable the coach tip until dismissed.
   */
  async markSkillsWalkthroughSeen(opts?: { skipped?: boolean }): Promise<void> {
    const email = this.auth.user()?.email?.trim().toLowerCase();
    this.seenSkillsWalkthrough.set(true);
    if (opts?.skipped && email) {
      try {
        // PAT-414 — use btoa(email) to avoid exposing email in plain text in localStorage key.
        localStorage.setItem(`${COACH_TIP_STORAGE}:${btoa(email)}`, 'pending');
      } catch {
        /* ignore */
      }
      this.showSkillsCoachTip.set(true);
    }
    if (!environment.claudeDbSyncEnabled || !this.auth.isLoggedIn()) return;
    const prefs = this.resolvedPreferences();
    try {
      await this.aiChat.putAccountPreferences(environment.chatApiBaseUrl, {
        defaultModel: {
          modelKey: prefs.defaultModel.modelKey,
          displayName: prefs.defaultModel.displayName,
          providerDisplayName: prefs.defaultModel.providerDisplayName,
        },
        fileUploadTypeIds: prefs.fileUploadTypeIds,
        themePreference: this.themeService.themePreference(),
        fontSize: this.fontSizeService.fontSize(),
        hasSeenSkillsWalkthrough: true,
      });
    } catch {
      /* offline */
    }
  }

  /** PAT-270 — reopen walkthrough from Help menu (does not clear seen flag). */
  requestSkillsWalkthroughReplay = signal(false);

  openSkillsWalkthroughFromMenu(): void {
    this.requestSkillsWalkthroughReplay.set(true);
  }

  clearSkillsWalkthroughReplayRequest(): void {
    this.requestSkillsWalkthroughReplay.set(false);
  }

  dismissSkillsCoachTip(): void {
    const email = this.auth.user()?.email?.trim().toLowerCase();
    this.showSkillsCoachTip.set(false);
    if (!email) return;
    try {
      // PAT-414 — use btoa(email) to avoid exposing email in plain text in localStorage key.
    localStorage.setItem(`${COACH_TIP_STORAGE}:${btoa(email)}`, 'done');
    } catch {
      /* ignore */
    }
  }

  private refreshCoachTipVisibility(email: string, walkthroughSeen: boolean): void {
    if (!walkthroughSeen) {
      this.showSkillsCoachTip.set(false);
      return;
    }
    try {
      // PAT-414 — use btoa(email) to avoid exposing email in plain text in localStorage key.
      const v = localStorage.getItem(`${COACH_TIP_STORAGE}:${btoa(email)}`);
      this.showSkillsCoachTip.set(v === 'pending');
    } catch {
      this.showSkillsCoachTip.set(false);
    }
  }
}
